import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { GoogleIcon } from "@/components/GoogleIcon";
import { supabase } from "@/lib/supabase";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/workspace";

  useEffect(() => {
    document.title = "Sign in — MACHA";
  }, []);

  const signInEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    navigate(next, { replace: true });
  };

  const signInGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}` },
    });
  };

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center px-5 py-16">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <Logo />
          <h1 className="mt-7 text-2xl font-semibold tracking-tight text-foreground">Welcome back</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Sign in to your MACHA account.</p>
        </div>

        <Button
          data-testid="login-google-btn"
          onClick={signInGoogle}
          variant="outline"
          className="mt-8 h-11 w-full justify-center gap-2.5 rounded-lg border-border text-[13.5px] font-medium"
        >
          <GoogleIcon className="h-[18px] w-[18px]" /> Continue with Google
        </Button>

        <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>

        <form className="space-y-4" onSubmit={signInEmail} aria-label="Sign in form">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[13px]">Email</Label>
            <Input id="email" type="email" autoComplete="email" placeholder="you@studio.com"
              value={email} onChange={(e) => setEmail(e.target.value)} required
              data-testid="login-email-input" className="h-10 rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-[13px]">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)} required
              data-testid="login-password-input" className="h-10 rounded-lg" />
          </div>
          <Button type="submit" disabled={loading}
            data-testid="login-submit-btn"
            className="h-10 w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="mt-8 text-center text-[13px] text-muted-foreground">
          New to MACHA?{" "}
          <Link to={`/signup?next=${encodeURIComponent(next)}`} className="text-foreground hover:text-primary">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
